export * from './sanctions';
export * from './smart-track-labels';
export * from './terms-versions';
export * from './error-codes';
export * from './sender-stats';
export * from './public-facts';

/**
 * Sentinel UUID for LLM-based classifications.
 * Used as default matched_rule_id when an LLM advisory remains authoritative.
 *
 * Must be a schema-valid UUID v4 (version nibble `4`, variant nibble in [8-b])
 * so it passes `z.string().uuid()` validation used by the strict LLM output
 * schema. Legacy value `00000000-0000-0000-0000-000000000001` was rejected by
 * Zod - see issue #278 for the full correctness fix.
 */
export const PUREBOX_LLM_RULE_ID = '00000000-0000-4000-8000-000000000001' as const

/**
 * Sentinel category key for suggestions with no labels (empty `labels[]`).
 *
 * This is the INTERNAL wire/DB aggregate key + `label` filter value only - it
 * is NOT a user-visible string. The backend emits this key in
 * `summary.by_label` and accepts it as the `label` query param to select rows
 * whose first label is null/empty. The frontend localises it to the
 * `cleanup.uncategorized` copy token (displayed as "General"), so reconciliation
 * stays exact without coupling the backend to i18n. See issue #476.
 */
export const UNCATEGORIZED_LABEL = 'Uncategorized' as const

// ---------------------------------------------------------------------------
// Time-metric constants (single source of truth for all screens)
// See docs/UI_UX_DESIGN_GUIDELINES.md § Time Metric Definitions
// ---------------------------------------------------------------------------

/**
 * Seconds of manual email-handling time saved per suggestion acted on.
 *
 * Used in every time-metric formula:
 *   - Estimated clutter time   = (archive + trash suggestions) × this value
 *   - Reclaimable time         = pending suggestions × this value
 *   - Reclaimed time           = applied suggestions × this value
 *
 * Changing this value updates ALL time displays across every screen.
 */
export const SECONDS_PER_SUGGESTION = 15

/**
 * Minimum confidence score (0–1) for a suggestion to appear in
 * user-facing counts and time estimates.
 *
 * Dashboard, cleanup page, and mobile bar all use this threshold.
 * Suggestions below this value still exist in the DB but are excluded
 * from headline numbers to avoid inflating the metric with low-quality items.
 */
export const CONFIDENCE_DISPLAY_THRESHOLD = 0.50

/**
 * Single shared visibility threshold for classification suggestions (issue #721).
 *
 * A finalized classification with confidence >= this value is persisted as a
 * user-visible `pending` suggestion; below it, the row is still persisted but as
 * a `low_confidence` bookkeeping marker (excluded from user-facing reads and
 * analytics). Every entry point - scan Classify stage, realtime, and reanalyze -
 * uses THIS constant and always persists; no path skips persistence based on
 * confidence. Any stricter *display* cutoff stays a read-time filter, never a
 * persist/skip decision.
 *
 * Replaces the former per-message default of 0.7 and the worker-local
 * `MIN_LLM_CONFIDENCE`.
 */
export const CLASSIFICATION_MIN_VISIBLE_CONFIDENCE = 0.5

/**
 * Maximum number of suggestions a single server-side batch-apply job will
 * resolve and act on in one request (issue #651).
 *
 * Applies to every server-side resolution path (whole-track apply, whole-
 * category apply, "apply all"): when a resolved cohort exceeds this cap, the
 * UI must say plainly how many will be applied rather than silently
 * truncating. Raising this cap requires worker-side chunking - see #651 "Out
 * of Scope".
 */
export const BATCH_APPLY_MAX_SUGGESTIONS = 500

// ---------------------------------------------------------------------------
// Display confidence remap (#389)
// ---------------------------------------------------------------------------

/** Raw confidence floor (below this, suggestions are low_confidence bookkeeping) */
const RAW_MIN = 0.50
/** Display scale lower bound */
const DISPLAY_MIN = 0.80
/** Scale factor: maps [RAW_MIN, 1.0] → [DISPLAY_MIN, 1.0] */
const DISPLAY_SCALE = (1.0 - DISPLAY_MIN) / (1.0 - RAW_MIN)

/**
 * Remap raw ML confidence (0.50-1.0) to a display-friendly scale (80-100%).
 *
 * Formula: display = 0.80 + (raw - 0.50) × 0.40
 *   raw 0.50 → 80%   raw 0.70 → 88%   raw 0.85 → 94%   raw 1.0 → 100%
 *
 * Clamps output to [0.80, 1.0]. Raw values below 0.50 map to 0.80.
 */
export function toDisplayConfidence(raw: number): number {
  const display = DISPLAY_MIN + (raw - RAW_MIN) * DISPLAY_SCALE
  return Math.min(1.0, Math.max(DISPLAY_MIN, Math.round(display * 100) / 100))
}

/**
 * Inverse: convert a display-scale confidence back to raw scale.
 * Used by the confidence slider to translate the UI value to an API query param.
 *
 * Clamps output to [0.50, 1.0].
 */
export function fromDisplayConfidence(display: number): number {
  const raw = RAW_MIN + (display - DISPLAY_MIN) / DISPLAY_SCALE
  return Math.min(1.0, Math.max(RAW_MIN, Math.round(raw * 100) / 100))
}
